import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, take, takeUntil } from 'rxjs';
import { RingostatService } from '../../../../../services/ringostat.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { IError } from '../../../../../interfaces';
import { DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations } from '../../../../../enum/constants';
import { DialogService } from '../../../../../services/dialog.service';

interface IRingostatControlForm {
  api_key: FormControl;
}

@Component({
  selector: 'app-integration-ringostat',
  templateUrl: './integration-ringostat.component.html',
  styleUrls: ['./integration-ringostat.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IntegrationRingostatComponent implements OnInit, OnDestroy {
  public form!: FormGroup<IRingostatControlForm>;
  private readonly destroy$ = new Subject<null>();
  public isFormSubmitted: boolean = false;
  public keys: any[] = [];

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    private readonly ringostatService: RingostatService,
    private readonly notificationService: NotificationService,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.getKeys();
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  initForm() {
    if (this.keys.length) {
      this.form = new FormGroup<IRingostatControlForm>({
        api_key: new FormControl(this.keys[0]?.api_key, Validators.required),
      });
    } else {
      this.form = new FormGroup<IRingostatControlForm>({
        api_key: new FormControl('', Validators.required),
      });
    }
  }

  onSubmit() {
    this.isFormSubmitted = true;

    if (this.form.valid) {
      const payload = this.form.getRawValue();

      if (this.keys.length) {
        this.ringostatService
          .putApiRingostat(payload, this.keys[0].id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notificationService.showSuccess(
                '',
                activeTranslations.notifications_texts.success.successfully_updated,
              );
              this.close.emit();
            },
            error: (e) => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
      } else {
        this.ringostatService
          .addApiRingostat(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notificationService.showSuccess(
                '',
                activeTranslations.notifications_texts.success.successfully_added,
              );
              this.close.emit();
            },
            error: (e) => {
              // this.notificationService.showError('Помилка', 'Даний API ключ вже підключений', 5000)
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
      }
    }
  }

  deleteApi() {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.integration-ringostat',
    });

    dialogRef.subscribe({
      next: (res) => {
        if (res) {
          this.ringostatService
            .deleteApiRingostat(this.keys[0].id)
            .pipe(takeUntil(this.destroy$), take(1))
            .subscribe({
              next: () => {
                this.notificationService.showSuccess(
                  '',
                  activeTranslations.notifications_texts.success.successfully_deleted,
                );
                this.close.emit();
              },
              error: (e) => {
                (e.error?.errors || []).forEach((err: IError) => {
                  this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
                });
              },
            });
        }
      },
      error: (e) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
        });
      },
    });
  }

  getKeys() {
    this.ringostatService
      .getKeys()
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe((v) => {
        this.keys = v;
        this.initForm();
      });
  }
}
